import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from 'components/Button';
import Table from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import {
  useShareSplits,
  useMutateShareSplit,
} from 'hooks/data/company/useShareSplits';
import { createShareSplitAddRoute } from 'adminConstants/routes';
import { Delete } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const StyledButton = styled(Button)({
  alignSelf: 'flex-end',
});

const TooltipWrapper = styled('div')({
  display: 'inline-flex',
});

type ShareSplit = {
  _id: string;
  date: Date;
  shareSplitFactor: number;
  sharePricePreSplit: number;
  sharePricePostSplit: number;
  sharesBefore: number;
  sharesAfter: number;
  canDelete: boolean;
};

const getColumns = (onRequestDelete: (shareSplit: ShareSplit) => void) => [
  {
    label: 'Date',
    key: 'date',
    sort: false,
    render: (elm: ShareSplit) => dateToLabel(elm.date),
  },
  {
    key: 'shareSplitFactor',
    label: 'Share split factor',
    sort: false,
  },
  {
    key: 'sharePricePreSplit',
    label: 'Original share price',
    sort: false,
    render: (elm: ShareSplit) =>
      numberToCurrencyString(elm.sharePricePreSplit, { unlimitedDp: true }),
  },
  {
    key: 'sharePricePostSplit',
    label: 'New share price',
    sort: false,
    render: (elm: ShareSplit) =>
      numberToCurrencyString(elm.sharePricePostSplit, { unlimitedDp: true }),
  },
  {
    key: 'sharesBefore',
    label: 'Shares before',
    render: (elm: ShareSplit) => numberToDisplayString(elm.sharesBefore),
  },
  {
    key: 'sharesAfter',
    label: 'Shares after',
    render: (elm: ShareSplit) => numberToDisplayString(elm.sharesAfter),
  },
  {
    key: 'actions',
    label: 'Actions',
    sort: false,
    render: (elm: ShareSplit) => {
      const tooltip = !elm.canDelete
        ? 'There has been an allocation or exit after the date of this share split. Please first delete the allocation or exit.'
        : undefined;

      const button = (
        <IconButton
          onClick={() => onRequestDelete(elm)}
          color="error"
          size="small"
          disabled={!elm.canDelete}
        >
          <Delete />
        </IconButton>
      );

      return tooltip ? (
        <Tooltip arrow title={tooltip}>
          {!elm.canDelete ? <TooltipWrapper>{button}</TooltipWrapper> : button}
        </Tooltip>
      ) : (
        button
      );
    },
  },
];

type Props = {
  companyId: string;
};

const ShareSplitRows: React.FC<Props> = ({ companyId }) => {
  const history = useHistory();
  const { shareSplits } = useShareSplits({ params: { companyId } });
  const { deleteShareSplit } = useMutateShareSplit();
  const deleteDisclosure = useDisclosure();
  const [canCreateShareSplit] = useHasPermissions([
    RolePermission.CreateShareSplit,
  ]);

  const handleRequestDelete = (elm: ShareSplit) => {
    deleteDisclosure.stageAction({ shareSplitId: elm._id });
    deleteDisclosure.onOpen();
  };

  const handleConfirmDelete = () => {
    deleteShareSplit.mutate(
      { shareSplitId: deleteDisclosure.actionProps.shareSplitId },
      {
        onSuccess: () => {
          deleteDisclosure.onClose();
        },
      },
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Table
        loading={shareSplits.isFetching}
        columns={getColumns(handleRequestDelete)}
        tablebody={
          shareSplits?.data?.map((split) => {
            return {
              ...split,
              date: new Date(split.date.toString()),
            };
          }) ?? []
        }
      />
      {canCreateShareSplit && (
        <StyledButton
          onClick={() => history.push(createShareSplitAddRoute(companyId))}
          variant="text"
          color="primary"
        >
          Add share split
        </StyledButton>
      )}

      <AlertDialog
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        open={deleteDisclosure.isOpen}
        title="Confirm delete"
        content={
          <p>
            This action cannot be undone. Are you sure you want to delete this
            share split?
          </p>
        }
        btnLabels={{ cancel: 'Go back', confirm: 'Delete' }}
        confirmBtnProps={{
          disabled: deleteShareSplit.isPending,
          color: 'error',
        }}
        cancelBtnProps={{ color: 'primary' }}
      />
    </Box>
  );
};

export default ShareSplitRows;
